import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined'

import NotificationSnackbar from '../components/ui/NotificationSnackbar'
import PageLoader from '../components/ui/PageLoader'
import SingleRecordWorkspace from '../components/ui/SingleRecordWorkspace'
import { CrmBadge } from '../components/ui'
import WorkOrderItemsSection from '../components/workorders/WorkOrderItemsSection'
import WorkOrderFooterSection from '../components/workorders/WorkOrderFooterSection'
import { fetchWorkOrderById, generateWorkOrderPdf } from '../services/workOrderServices'
import { useSettings } from '../context/SettingsContext'
import { displayCurrency } from '../utils/currencyUtils'
import { formatMoney } from '../utils/formatters'

const parseJsonMaybe = (value) => {
  if (!value) return null
  if (typeof value === 'object') return value
  if (typeof value !== 'string') return null
  try { return JSON.parse(value) } catch { return null }
}

const normalizeItems = (data) => {
  const rawItems = data?.items || data?.work_order_items || data?.workOrderItems || data?.order_items || data?.line_items || []
  const parsedItems = parseJsonMaybe(rawItems)
  const list = Array.isArray(rawItems) ? rawItems : Array.isArray(parsedItems) ? parsedItems : []
  return list.map((it) => {
    const quantity = Number(it.quantity ?? it.qty ?? 0)
    const unitPrice = Number(it.unit_price ?? it.price ?? it.rate ?? 0)
    const discount = Number(it.discount ?? it.discount_amount ?? 0)
    const tax = Number(it.tax ?? it.tax_amount ?? it.gst_amount ?? 0)
    return {
      ...it,
      product_name: it.product_name || it.name || it.description || '—',
      brand: it.brand || it.product_brand || '—',
      quantity,
      unit_price: unitPrice,
      discount,
      tax,
      line_total: Number(it.line_total ?? it.total ?? (quantity * unitPrice - discount + tax) ?? 0),
    }
  })
}

function WorkOrderDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { settings } = useSettings()
  const currency = displayCurrency(settings?.currency_code)
  const isGeneralBusiness = String(settings?.business_type || 'GENERAL').toUpperCase() === 'GENERAL'

  const [workOrder, setWorkOrder] = useState(null)
  const [loading, setLoading] = useState(true)
  const [downloadingPdf, setDownloadingPdf] = useState(false)
  const [notif, setNotif] = useState({ open: false, message: '', severity: 'success' })

  const showNotification = useCallback((message, severity = 'success') => {
    setNotif({ open: true, message, severity })
  }, [])

  const loadWorkOrder = useCallback(async () => {
    try {
      setLoading(true)
      const data = await fetchWorkOrderById(id)
      data.total_amount = Number(data.total_amount || 0)
      data.items = normalizeItems(data)
      const discountSum = data.items.reduce((sum, item) => sum + (Number(item.line_total || 0) < 0 ? Number(item.line_total || 0) : 0), 0)
      data.items = data.items.filter((item) => Number(item.line_total || 0) >= 0)
      data.display_taxable_subtotal = Number(data.display_taxable_subtotal || (Number(data.subtotal || 0) + Math.abs(discountSum)))
      data._computed_discount = Number(data._computed_discount || Math.abs(discountSum))
      setWorkOrder(data)
    } catch (error) {
      showNotification(error?.response?.data?.error || 'Failed to load work order', 'error')
    } finally {
      setLoading(false)
    }
  }, [id, showNotification])

  useEffect(() => { loadWorkOrder() }, [loadWorkOrder])

  const handleDownloadPdf = async () => {
    if (downloadingPdf) return
    try {
      setDownloadingPdf(true)
      await generateWorkOrderPdf(id)
    } catch (error) {
      showNotification(error?.response?.data?.error || 'Failed to download work order PDF', 'error')
    } finally {
      setDownloadingPdf(false)
    }
  }



  const taxes = useMemo(() => (workOrder?.items || []).reduce((sum, item) => sum + Number(item.tax || item.tax_amount || item.gst_amount || 0), 0), [workOrder?.items])

  if (loading) return <PageLoader message="Loading work order..." minHeight={300} />
  if (!workOrder) return <div className="quotation-loading">Work Order not found</div>

  const customerName = workOrder.customer_name || workOrder.first_name || workOrder.company_name || 'Customer'
  const workspaceRecord = {
    ...workOrder,
    customer_name: customerName,
    customer_email: workOrder.email || '',
    customer_phone: workOrder.phone_number || '',
    total_display: formatMoney(Number(workOrder.total_amount || workOrder.grand_total || 0)),
  }

  const fields = [
    { key: 'status', label: 'Status', type: 'status', readOnly: true },
    { key: 'customer_name', label: 'Contact', readOnly: true },
    { key: 'company_name', label: 'Company', readOnly: true },
    { key: 'customer_email', label: 'Email', readOnly: true },
    { key: 'customer_phone', label: 'Phone', readOnly: true },
    { key: 'issue_date', label: 'Issue date', type: 'date', readOnly: true },
    { key: 'notes', label: 'Notes', type: 'textarea', readOnly: true },
    { key: 'event_name', label: 'Event name', readOnly: true },
    { key: 'event_date', label: 'Event date', type: 'date', readOnly: true },
    { key: 'event_time', label: 'Event time', type: 'time', readOnly: true },
    { key: 'event_location', label: 'Venue', readOnly: true },
    { key: 'pax', label: 'PAX', type: 'number', readOnly: true },
    { key: 'total_display', label: 'Grand total', readOnly: true },
  ]

  const related = [
    workOrder.quotation_id ? { key: 'quotation', label: workOrder.quotation_number || `Quotation #${workOrder.quotation_id}`, meta: 'Quotation', path: `/quotations/${workOrder.quotation_id}` } : null,
    workOrder.kot?.id ? { key: 'kot', label: `KOT #${workOrder.kot.id}`, meta: workOrder.kot.status, path: `/kots?focusKotId=${workOrder.kot.id}` } : null,
    workOrder.delivery?.id ? { key: 'delivery', label: `Delivery #${workOrder.delivery.id}`, meta: workOrder.delivery.status, path: `/deliveries?focusDeliveryId=${workOrder.delivery.id}` } : null,
    workOrder.proforma_invoice?.id ? { key: 'proforma', label: workOrder.proforma_invoice.proforma_number || `Proforma #${workOrder.proforma_invoice.id}`, meta: workOrder.proforma_invoice.status, path: `/proforma-invoices/${workOrder.proforma_invoice.id}` } : null,
    workOrder.invoice?.id ? { key: 'invoice', label: workOrder.invoice.invoice_number || `Invoice #${workOrder.invoice.id}`, meta: workOrder.invoice.status, path: `/invoices/${workOrder.invoice.id}` } : null,
  ].filter(Boolean)

  const cards = [
    { id: 'work-order-client', title: 'Client details', position: 'left', deletable: false, editable: false, fieldKeys: ['customer_name', 'company_name', 'customer_email', 'customer_phone'] },
    { id: 'work-order-details', title: 'Work Order details', position: 'left', deletable: false, editable: false, fieldKeys: ['issue_date', 'notes'] },
    ...(!isGeneralBusiness && (workOrder.quotation_mode === 'CATERING' || workOrder.mode === 'CATERING') ? [{ id: 'work-order-event', title: 'Event details', position: 'left', deletable: false, editable: false, fieldKeys: ['event_name', 'event_date', 'event_time', 'event_location', 'pax'] }] : []),
    { id: 'work-order-actions', title: 'Document actions', position: 'right', fixed: true, disableDrag: true, deletable: false, allowSettings: false, titleEditable: false, fieldKeys: [], customContent: () => <div className="record-action-buttons"><button type="button" className="hs-listing__create" title={downloadingPdf ? 'Downloading PDF…' : 'Download PDF'} aria-label="Download work order PDF" disabled={downloadingPdf} onClick={handleDownloadPdf}>{downloadingPdf ? <span className="record-action-loading" aria-hidden="true">…</span> : <PictureAsPdfOutlinedIcon />}</button></div> },
    { id: 'work-order-related', title: 'Related documents', position: 'right', deletable: false, editable: false, fieldKeys: [], customContent: () => <div className="quotation-related-documents">{related.length ? related.map((doc) => <button type="button" key={doc.key} className="quotation-related-document" onClick={() => navigate(doc.path)}><span>{doc.label}</span><CrmBadge tone="neutral">{doc.meta || 'Open'}</CrmBadge></button>) : <p className="quotation-related-empty">No related documents yet.</p>}</div> },
    { id: 'work-order-total', title: 'Work Order total', position: 'right', deletable: false, editable: false, fieldKeys: ['total_display'] },
  ]

  return <>
    <SingleRecordWorkspace
      storageKey={`jdh-crm:record:work-order:${id}`}
      backTo="/workorders"
      backLabel="Work Orders"
      title={workOrder.work_order_number || `Work Order #${id}`}
      record={workspaceRecord}
      fields={fields}
      initialCards={cards}
      summaryCard={{ backTo: '/workorders', backLabel: 'Work Orders', displayTitle: workOrder.work_order_number || `Work Order #${id}`, subtitle: customerName, fieldKeys: ['status', 'issue_date'], editable: false }}
      hidePageHeader
      centerLabel={`Order Lines (${workOrder.items.length})`}
      centerContent={<div className="quotation-order-lines-workspace"><WorkOrderItemsSection items={workOrder.items} /></div>}
      centerSummary={<div className="quotation-card"><WorkOrderFooterSection subtotal={Number(workOrder.display_taxable_subtotal || workOrder.subtotal || 0)} discount={Number(workOrder._computed_discount || 0)} taxes={Number(taxes || 0)} total={Number(workOrder.total_amount || 0)} currency={currency} /></div>}
    />
    <NotificationSnackbar open={notif.open} message={notif.message} severity={notif.severity} onClose={() => setNotif((current) => ({ ...current, open: false }))} />
  </>
}

export default WorkOrderDetail
